import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  doReportingGetReportCsv,
  doReportingMergeDraftFilterValues,
  doReportingShowReportFilterModal,
  doReportingSetReportVisualsSize,
  doReportingGetReportListData,
} from 'store/actions';
import {
  selectRptReportFilterOptions,
  selectRptReportFilterValues,
  selectRptReportHasVisuals,
  selectRptReportName,
  selectRptReportTotalCount,
  selectRptReportVisualsSize,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';
import { IconButton } from 'components/IconButton';
import { QuickOptionField } from 'components/Field/QuickOptionField';
import { smartText } from 'lib/str';
import { ReportingVisualsSizeEnum } from 'types/types';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';
import { ButtonGroup } from 'components/ButtonGroup';
import { LayoutChangeEvent } from 'react-native';
import { getBreakpoint } from 'hooks/useScreenBreakpoints';

type Report = ReportTypes[ReportEnum.checkscompleted];

const screenBreakpoints = [
  {
    width: 400,
    key: 1,
  },
  {
    width: 450,
    key: 2,
  },
  {
    width: 550,
    key: 3,
  },
  {
    width: 700,
    key: 4,
  },
  {
    width: 850,
    key: 5,
  },
];

export const Header: React.FC<{ reportKey: string }> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const visualsSize = useSelector(selectRptReportVisualsSize);
  const hasVisuals = useSelector((state) =>
    selectRptReportHasVisuals(state, reportKey),
  );
  const reportName = useSelector((state) =>
    selectRptReportName(state, reportKey),
  );
  const reportTotalCount = useSelector((state) =>
    selectRptReportTotalCount(state, reportKey),
  );

  const filterValues = useSelector((state) =>
    selectRptReportFilterValues(state, reportKey),
  )! as Report['filterValues'];
  const filterOptions = useSelector((state) =>
    selectRptReportFilterOptions(state, reportKey),
  )! as Report['filterOptions'];

  const handleLoadReportCsv = React.useCallback(() => {
    dispatch(
      doReportingGetReportCsv({
        key: reportKey,
      }),
    );
  }, [dispatch, reportKey]);

  const handleFilterChange = React.useCallback(
    (mergeFilterValues: Partial<Report['filterValues']>) => {
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: mergeFilterValues,
          loadData: true,
        }),
      );
    },
    [dispatch, reportKey],
  );

  // Search field
  const handleSearchChange = React.useCallback(
    (newSearchValue: string) =>
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: { search: newSearchValue },
          loadData: true,
        }),
      ),
    [dispatch, reportKey],
  );

  const filteredOrganisations = filterValues.organisationIds.length
    ? filterOptions.organisations.filter((item) =>
        filterValues.organisationIds.includes(item.value),
      )
    : filterOptions.organisations.length === 1
      ? filterOptions.organisations
      : [];

  const [breakpoint, setBreakpoint] = React.useState<number>(1);
  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) =>
      setBreakpoint(
        getBreakpoint(screenBreakpoints, event.nativeEvent.layout.width) || 1,
      ),
    [],
  );

  return (
    <>
      <PageHeader
        title={reportName}
        subtitle={
          pluralise(
            reportTotalCount,
            '{x} Result',
            '{x} Results',
            'No Results',
          ) +
          (filteredOrganisations.length > 1
            ? ` • ${filteredOrganisations.length} Organisations`
            : filteredOrganisations.length === 1
              ? ` • ${filteredOrganisations[0]!.name}`
              : ' • All Organisations')
        }
        showSidebarButton
        menuRight={{
          iconButtonConfig: { icon: ICONS.EllipsisLight },
          options: [
            {
              text: 'Export List as CSV',
              icon: ICONS.ArrowDownLight,
              onPress: handleLoadReportCsv,
            },
            <CardRow
              key="visualsOptionsRow"
              label="Visuals Options"
              disabled={!hasVisuals}
              navigationIcon={ICONS.ChartBarDuotone}
              navigationIconColor={theme.colors.black}
              style={{
                width: 250,
                minHeight: 44,
                backgroundColor: '#fafafa',
              }}
              hoveredStyle={{ backgroundColor: '#f1f1f1' }}
              childrenStyle={{}}
              menuOptions={
                hasVisuals
                  ? [
                      {
                        text: 'Large Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.large
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.large,
                            }),
                          ),
                      },
                      {
                        text: 'Small Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.small
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.small,
                            }),
                          ),
                      },
                      {
                        text: 'Hide Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.none
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.none,
                            }),
                          ),
                      },
                      smartText(
                        'Some reports include visuals like charts and graphs. They only appear if they have data to show',
                      ),
                    ]
                  : undefined
              }
              showMenuOnPress
              hideMenuButton
            />,
          ],
        }}
      />
      <PageToolbar
        onLayout={handleLayout}
        onSearch={handleSearchChange}
        searchValue={filterValues.search}
        searchPlaceholder={`Search ${reportName}`}
        searchOnSubmit
      >
        <ButtonGroup>
          {breakpoint > 1 && (
            <QuickOptionField
              labelNone="All Checks"
              labelAll="All Checks"
              labelMultiple="{x} Checks"
              modalTitle="Checks"
              options={filterOptions.checks.map((item) => ({
                name: item.name,
                value: item.value,
                groupName: item.deleted ? 'Deleted Checks' : 'Current Checks',
              }))}
              values={filterValues.checks}
              enableSelectAll
              buttonMaxWidth={150}
              onChange={(newValue) => handleFilterChange({ checks: newValue })}
              modal
              modalSize="small"
              modalSearchable
              multiSelect
            />
          )}
          {breakpoint > 2 && (
            <QuickOptionField
              labelNone="All Elements"
              labelAll="All Elements"
              labelMultiple="{x} Elements"
              modalTitle="Elements"
              options={filterOptions.elements.map((item) => ({
                name: item.name,
                value: item.value,
                groupName: item.deleted
                  ? `Deleted Elements${!item.checks ? ' Without Checks' : ''}`
                  : `Current Elements${!item.checks ? ' Without Checks' : ''}`,
              }))}
              values={filterValues.elements}
              enableSelectAll
              buttonMaxWidth={150}
              onChange={(newValue) =>
                handleFilterChange({ elements: newValue })
              }
              modal
              modalSize="small"
              modalSearchable
              multiSelect
            />
          )}
          {breakpoint > 3 && (
            <QuickOptionField
              options={filterOptions.statuses.map((item) => ({
                name: item.name,
                value: item.value,
                icon: item.icon,
                iconColor: `#${item.color}`,
              }))}
              values={filterValues.statuses}
              labelNone="All Statuses"
              labelAll="All Statuses"
              labelMultiple="{x} Statuses"
              enableSelectAll
              menuWidth={180}
              buttonMaxWidth={150}
              onChange={(newValue) =>
                handleFilterChange({ statuses: newValue })
              }
            />
          )}
          <IconButton
            icon={ICONS.RotateRightLight}
            onPress={() =>
              dispatch(
                doReportingGetReportListData({
                  key: reportKey,
                  nextPage: false,
                  closeModals: false,
                  overlayLoader: true,
                }),
              )
            }
          />
          <IconButton
            icon={ICONS.SlidersUpLight}
            onPress={() =>
              dispatch(doReportingShowReportFilterModal({ key: reportKey }))
            }
            showBadge={
              !!filterValues.checks.length ||
              !!filterValues.elements.length ||
              !!filterValues.organisationIds.length ||
              !!filterValues.statuses.length ||
              !!filterValues.dateFrom ||
              !!filterValues.dateTo
            }
          />
        </ButtonGroup>
      </PageToolbar>
    </>
  );
};
